import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  Eye, 
  Calendar,
  DollarSign,
  MapPin,
  ArrowUpDown
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { emendaService } from '../services/emendaService';
import { Emenda } from '../types/emenda';
import { motion, AnimatePresence } from 'motion/react';

export const EmendasList = () => {
  const [emendas, setEmendas] = useState<Emenda[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState('Todas');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmendas();
  }, []);

  const fetchEmendas = async () => {
    setLoading(true);
    try {
      const data = await emendaService.getAll();
      setEmendas(data);
    } catch (err) {
      console.error('Failed to fetch emendas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, titulo: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a emenda "${titulo}"? Esta ação não pode ser desfeita.`)) {
      try {
        await emendaService.delete(id);
        setEmendas(prev => prev.filter(e => e.id !== id));
      } catch (err) {
        alert('Erro ao excluir emenda. Tente novamente.');
      }
    }
  };

  const filteredEmendas = emendas.filter(e => {
    const matchesSearch = e.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         e.municipio.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterArea === 'Todas' || e.area_investimento === filterArea;
    return matchesSearch && matchesFilter;
  });

  const uniqueAreas = ['Todas', ...new Set(emendas.map(e => e.area_investimento))];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Gerenciar Emendas</h1>
          <p className="text-slate-500 mt-1 font-medium">Listagem completa e controle das emendas parlamentares.</p>
        </div>
        <Link 
          to="/admin/emendas/nova"
          className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-sm flex items-center gap-2 hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 active:scale-95"
        >
          <Plus size={20} />
          Nova Emenda
        </Link>
      </div>

      {/* Filters & Search */}
      <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Pesquisar por título ou município..." 
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500/10 transition-all outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative w-full md:w-64">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <select 
              className="w-full pl-12 pr-10 py-3 bg-slate-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500/10 transition-all outline-none appearance-none cursor-pointer"
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
            >
              {uniqueAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] border-b border-slate-100">
                <th className="px-8 py-6">
                  <div className="flex items-center gap-2 cursor-pointer hover:text-slate-600 transition-colors">
                    Informações da Emenda
                    <ArrowUpDown size={12} />
                  </div>
                </th>
                <th className="px-8 py-6">Valor</th>
                <th className="px-8 py-6">Localidade</th>
                <th className="px-8 py-6">Status/Docs</th>
                <th className="px-8 py-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <AnimatePresence mode='popLayout'>
                {loading ? (
                  Array(5).fill(0).map((_, i) => (
                    <motion.tr 
                      key={`skeleton-${i}`} 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="animate-pulse"
                    >
                      <td className="px-8 py-6"><div className="h-10 bg-slate-100 rounded-xl w-64" /></td>
                      <td className="px-8 py-6"><div className="h-6 bg-slate-100 rounded-xl w-24" /></td>
                      <td className="px-8 py-6"><div className="h-6 bg-slate-100 rounded-xl w-32" /></td>
                      <td className="px-8 py-6"><div className="h-6 bg-slate-100 rounded-xl w-20" /></td>
                      <td className="px-8 py-6"><div className="h-10 bg-slate-100 rounded-xl w-24 ml-auto" /></td>
                    </motion.tr>
                  ))
                ) : filteredEmendas.length > 0 ? (
                  filteredEmendas.map((emenda) => (
                    <motion.tr 
                      key={emenda.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-slate-50/80 transition-all group"
                    >
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 font-black group-hover:scale-110 transition-transform flex-shrink-0">
                            {emenda.area_investimento.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{emenda.titulo}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] font-black uppercase tracking-widest text-blue-500 bg-blue-50 px-2 py-0.5 rounded-md">
                                {emenda.area_investimento}
                              </span>
                              <span className="text-slate-300">|</span>
                              <span className="text-[10px] font-bold text-slate-400">
                                {new Date(emenda.data_publicacao).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-sm font-black text-slate-800 flex items-center gap-1">
                          <span className="text-slate-400 font-medium">R$</span>
                          {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(emenda.valor)}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                            <MapPin size={14} className="text-slate-300" />
                            {emenda.municipality || emenda.municipio}
                          </span>
                          <span className="text-[10px] font-black uppercase text-slate-400 ml-5">{emenda.estado}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2">
                           {emenda.documentos && emenda.documentos.length > 0 ? (
                             <span className="bg-emerald-50 text-emerald-600 text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1">
                               {emenda.documentos.length} DOCS
                             </span>
                           ) : (
                             <span className="bg-slate-50 text-slate-400 text-[10px] font-black px-2 py-1 rounded-lg">
                               SEM DOCS
                             </span>
                           )}
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                          <button 
                            onClick={() => navigate(`/admin/emendas/editar/${emenda.id}`)}
                            className="p-3 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:shadow-lg transition-all"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(emenda.id, emenda.titulo)}
                            className="p-3 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-red-600 hover:border-red-200 hover:shadow-lg transition-all"
                            title="Excluir"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan={5} className="px-8 py-20 text-center">
                      <div className="max-w-xs mx-auto space-y-4">
                        <div className="w-16 h-16 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto text-slate-300">
                          <Search size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900">Nenhum resultado</h3>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed">
                          Não encontramos nenhuma emenda com os filtros aplicados. Tente ajustar sua busca.
                        </p>
                        <button 
                          onClick={() => { setSearchTerm(''); setFilterArea('Todas'); }}
                          className="text-blue-600 text-sm font-black hover:underline"
                        >
                          Limpar Filtros
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
        
        {/* Pagination Placeholder */}
        <div className="px-8 py-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Mostrando {filteredEmendas.length} de {emendas.length} registros
          </p>
          <div className="flex items-center gap-2">
            <button disabled className="p-2 text-slate-300 cursor-not-allowed"><MoreVertical size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};
